<!-- This is called PolicyCollectionDrawer instead of PolicyValidationRulesDrawer,
 because at some point it will eventually hold more than just the validation rules -->
<script setup lang="ts">
import type { RulesPolicyDocument, ValidationRule } from '#layers/access/types/permissions/policy';
import type { BlockRootContext } from '#layers/block-engine/public';
import type { FormBlockSuccessEventPayload } from '#layers/blocks/public';
import type { AnyItem, CompositePrimaryKey, GlobalCollectionId } from '#layers/data-model/public';
import { computed, ref } from 'vue';
import { useWorkspaceSchemaStore } from '#layers/data-model/public';
import { provideRelatedItemDrawer } from '#layers/filters/public';
import { useCollectionItemPicker } from '#layers/listing/public';
import { PageShell } from '#layers/ui-workspace/public';
import { DrawerPage, PageSlotProvider, TeleportPageActions } from '#layers/ui/public';
import PolicyValidationRules from './PolicyValidationRules.vue';

export interface PolicyDrawerProps {
	collectionId: GlobalCollectionId;
	itemId?: CompositePrimaryKey;
	itemEdits?: AnyItem;
	formMode?: 'submit' | 'emit';
	content: RulesPolicyDocument;
}

const { collectionId, itemId, formMode, itemEdits, content } = defineProps<PolicyDrawerProps>();
const emit = defineEmits<{
	close: [payload?: FormBlockSuccessEventPayload];
}>();

provideRelatedItemDrawer(useCollectionItemPicker());

const { findCollectionById } = useWorkspaceSchemaStore();
const collection = computed(() => findCollectionById(collectionId));
const collectionName = computed<string>(() => collection.value?.apiName ?? '');

const blockContext = computed<BlockRootContext>(() => ({
	itemId,
	formMode,
	itemEdits,
	pageType: 'detail',
	pageIsFullWidth: false,
	pageDisplayMode: 'drawer',
}));

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- seeds the drawer's editable copy; it is written back on save
const validationRules = ref<ValidationRule[]>(content.validationRules?.[collectionName.value] ?? []);

function save() {
	emit('close', {
		key: itemId!,
		data: {
			validationRules: validationRules.value,
		},
	});
}
</script>

<template>
	<DrawerPage
		:attach-to-route="false"
		:ui="{ container: 'gap-0 2xl:w-200', bodyContent: 'p-0' }"
		@close="() => emit('close')"
	>
		<template #header>
			<slot name="header">
				<h2 class="flex-1 text-lg font-semibold">
					<span class="flex-1 font-mono text-lg font-medium">{{ collectionName }}</span> → {{ $t(`policy.validation_rules.title`) }}
				</h2>
			</slot>
		</template>
		<PageSlotProvider>
			<PageShell :context="blockContext">
				<div class="flex flex-col gap-4">
					<div class="flex flex-col gap-2">
						<DNotice color="info" :description="$t('policy.validation_rules.information_notice')" />
						<PolicyValidationRules v-if="collection" v-model="validationRules" :collection-id />
					</div>
				</div>
			</PageShell>
			<TeleportPageActions>
				<DButton color="neutral" variant="outline" :label="$t('common.cancel')" @click="emit('close')" />
				<DButton :label="$t('common.save')" @click="save" />
			</TeleportPageActions>
		</PageSlotProvider>
	</DrawerPage>
</template>
